import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
} from "recharts";
import { format, subDays } from "date-fns";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import ChartCard from "./ChartCard";
import CloseIcon from "@mui/icons-material/Close"; // add this import at top
const dateOptions = [
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 30 Days", value: "30d" },
  { label: "This Month", value: "month" },
  { label: "Custom Range", value: "custom" },
];

const formatYAxis = (value) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value;
};

const PerDayLineChartVoucher = ({
  title,
  color = "#29b6f6",
  dataKey = "VoucherData",
}) => {
  const [data, setData] = useState([]);
  const [rangeType, setRangeType] = useState("30d");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("Last 30 Days");

  const [page, setPage] = useState(0);
  const chunkSize = 7;

  const { company_id } = useSelector((state) => state.GlobalVariables);

  const handleMenuOpen = (event) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleRangeChange = (type) => {
    setRangeType(type);
    setPage(0);
    const selected = dateOptions.find((d) => d.value === type);
    setSelectedLabel(selected?.label || "");

    if (type === "custom") {
      setCustomStart(null);   // Reset start date
    setCustomEnd(null);
      setCustomDialogOpen(true);
    } else {
      setCustomDialogOpen(false);
    }
    handleMenuClose();
  };

  const getDateRange = () => {
    const today = new Date();
    let startDate, endDate;

    switch (rangeType) {
      case "7d":
        startDate = subDays(today, 6);
        endDate = today;
        setCustomStart(null);
        setCustomEnd(null);
        break;
      case "30d":
        startDate = subDays(today, 29);
        endDate = today;
        setCustomStart(null);
        setCustomEnd(null);
        break;
      case "month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = today;
        setCustomStart(null);
        setCustomEnd(null);
        break;
      case "custom":
        if (customStart && customEnd) {
          startDate = new Date(customStart);
          endDate = new Date(customEnd);
        }
        break;
      default:
        startDate = null;
        endDate = null;
    }

    return {
      start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
      end_date: endDate ? format(endDate, "yyyy-MM-dd") : null,
    };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { start_date, end_date } = getDateRange();
      const payload = {
        company_id,
        ...(start_date && end_date && { start_date, end_date }),
      };

      const result = await configServ.getDailyVoucherCount(payload);
      if (result.status) {
        const raw = result[dataKey] || [];
        const parsed = raw.map((item) => ({
          day: new Date(item.label).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "2-digit",
          }),
          value: parseFloat(item.total_amount),
        }));
        setData(parsed.reverse());
        setPage(0);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (rangeType !== "custom" || (customStart && customEnd)) {
      fetchData();
    }
  }, [rangeType, customStart, customEnd]);

  const totalPages = Math.ceil(data.length / chunkSize);
  const startIndex = page * chunkSize;
  const paginatedData = data
    .slice(startIndex, startIndex + chunkSize)
    .reverse();

  return (
    <ChartCard
      title={
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedLabel}
            </Typography>
          </Box>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      }
      loading={loading}
    >
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {dateOptions.map((opt) => (
          <MenuItem
            key={opt.value}
            onClick={() => handleRangeChange(opt.value)}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      <Dialog
        open={customDialogOpen}
        onClose={() => setCustomDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Select Custom Range
          <IconButton
            edge="end"
            onClick={() => setCustomDialogOpen(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
        >
          <TextField
            label="Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={customStart || ""}
            onChange={(e) => setCustomStart(e.target.value)}
          />
          <TextField
            label="End Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={customEnd || ""}
            onChange={(e) => setCustomEnd(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={() => {
              setCustomDialogOpen(false);
              setSelectedLabel(`Custom: ${customStart} to ${customEnd}`);
              fetchData();
            }}
            disabled={!customStart || !customEnd}
          >
            Apply
          </Button>
        </DialogContent>
      </Dialog>

      {!loading && (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={paginatedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="day"
                interval={0}
                angle={-32}
                dy={4}
                textAnchor="end"
                height={60}
              />
              <YAxis tickFormatter={formatYAxis} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setPage(page + 1)}
              disabled={page + 1 >= totalPages}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </ChartCard>
  );
};

export default PerDayLineChartVoucher;
